import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { apiLogin, apiGetMe, silentRefresh } from "../api/client";
import { isTokenFresh } from "../utils/jwt";
import { safeSetItem, safeGetItem, safeRemoveItem } from "../utils/safe-storage";
import { onSessionExpired, onForceLogout } from "../utils/session-events";

export interface AuthUser {
  id?: string;
  username: string;
  displayName: string;
  roleNames: string[];
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  loading: boolean;
  isExpiring: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => void;
  isAuthed: boolean;
}

const AuthContext = createContext<AuthState>({
  token: null,
  user: null,
  loading: true,
  isExpiring: false,
  login: async () => {},
  loginWithToken: async () => {},
  logout: () => {},
  isAuthed: false,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isExpiring, setIsExpiring] = useState(false);

  // 监听会话过期事件：401 拦截器在跳转登录前触发
  useEffect(() => {
    return onSessionExpired(() => {
      setIsExpiring(true);
    });
  }, []);

  // 监听强制登出事件：401 拦截器清除 localStorage token 后触发，同步清除 React 状态
  useEffect(() => {
    return onForceLogout(() => {
      setToken(null);
      setUser(null);
    });
  }, []);

  // 启动顺序：
  //   ① 本地已有**未过期**的 access token → 直接用它（**不打** /auth/refresh）；
  //   ② 没有、或已过期 → 走 Phase 0 跨子域静默刷新（共享 HttpOnly Cookie 自动带上）；
  //   ③ 刷新失败（未登录 / 跨子域未授权）→ 降级为同子域 localStorage 兜底（旧会话/本地调试）。
  //
  // 为什么先看本地 token 的剩余寿命：`/auth/refresh` 是**轮换型**刷新，启动流程与 401 拦截器
  // 若并发各刷一次，会让其中一条必然 401 —— 而「已登录用户刷新页面 / 跳转」是绝大多数场景，
  // 本地 token 通常还很新鲜，压根不需要刷新（2026-09-25 修复；线上那条
  // `401 POST /api/v1/auth/refresh` 即来源于此）。跨子域 SSO 不受影响：无本地 token 时仍照旧刷新。
  useEffect(() => {
    let alive = true;
    (async () => {
      const cached = safeGetItem("accessToken");
      let active = cached && isTokenFresh(cached) ? cached : null;
      if (!active) {
        try {
          active = await silentRefresh();
        } catch {
          // 无共享 Cookie（未登录或跨子域未授权）→ 退回本地 token（可能已过期，交给 401 拦截器处理）
          active = cached;
        }
      }
      if (!alive) return;
      if (active) {
        setToken(active);
        // 角色以服务端为准：/auth/me 一次校正（失败则沿用 localStorage 里的 profile，不阻断启动）
        try {
          const me = await apiGetMe();
          const u = me.data?.data?.user || me.data?.user;
          if (!alive) return;
          if (u) {
            const profile: AuthUser = {
              id: u.id || undefined,
              username: u.username || "unknown",
              displayName: u.displayName || u.username || "用户",
              roleNames: Array.isArray(u.roleNames) ? u.roleNames : [],
            };
            safeSetItem("userProfile", JSON.stringify(profile));
            setUser(profile);
          }
        } catch {
          // /auth/me 失败（含被 401 拦截器接管的场景）→ 沿用已有 profile
        }
      }
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // When token changes, extract user info from stored profile
  useEffect(() => {
    if (token) {
      try {
        const stored = localStorage.getItem("userProfile");
        if (stored) setUser(JSON.parse(stored));
        else setUser({ id: "user-admin-001", username: "admin", displayName: "管理员", roleNames: ["系统管理员"] });
      } catch {
        setUser({ id: "user-admin-001", username: "admin", displayName: "管理员", roleNames: ["系统管理员"] });
      }
    } else {
      setUser(null);
    }
  }, [token]);

  const login = useCallback(async (username: string, password: string) => {
    const resp = await apiLogin({ username, password });
    const t = resp.data?.data?.accessToken || resp.data?.accessToken;
    if (!t) throw new Error("登录返回异常");
    safeSetItem("accessToken", t);
    // Store user profile from login response (backend returns roleNames + roles)
    const backendUser = resp.data?.data?.user || resp.data?.user;
    const profile: AuthUser = {
      id: backendUser?.id || undefined,
      username: backendUser?.username || username,
      displayName: backendUser?.displayName || username,
      roleNames: Array.isArray(backendUser?.roleNames) && backendUser.roleNames.length > 0
        ? backendUser.roleNames
        : username === "admin" ? ["系统管理员"] : [],
    };
    localStorage.setItem("userProfile", JSON.stringify(profile));
    setToken(t);
    setUser(profile);
  }, []);

  // 飞书 SSO：网关签发 token 后免密进入，并通过 /auth/me 拉取用户资料
  const loginWithToken = useCallback(async (token: string) => {
    localStorage.setItem("accessToken", token);
    setToken(token);
    try {
      const resp = await apiGetMe();
      const u = resp.data?.data?.user || resp.data?.user;
      const profile: AuthUser = {
        id: u?.id || undefined,
        username: u?.username || "feishu-user",
        displayName: u?.displayName || "飞书用户",
        roleNames: Array.isArray(u?.roleNames) && u.roleNames.length > 0 ? u.roleNames : ["系统管理员"],
      };
      safeSetItem("userProfile", JSON.stringify(profile));
      setUser(profile);
    } catch {
      setUser({ username: "feishu-user", displayName: "飞书用户", roleNames: [] });
    }
  }, []);

  const logout = useCallback(() => {
    // Phase 0：同时清除跨子域共享 Cookie（Domain=.co-seek.cn），否则静默刷新会再次登录
    document.cookie = "rt=; Domain=.co-seek.cn; Path=/; Max-Age=0; Secure; SameSite=Lax";
    safeRemoveItem("accessToken");
    safeRemoveItem("userProfile");
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ token, user, loading, isExpiring, login, loginWithToken, logout, isAuthed: !!token }}
    >
      {children}
    </AuthContext.Provider>
  );
}
